import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { ShieldCheck, Loader2 } from 'lucide-react';

/**
 * ManagerPinDialog - Prompts for manager PIN override for sensitive actions
 * @param {boolean} open - Whether dialog is open
 * @param {function} onClose - Called when dialog closes
 * @param {function} onConfirm - Called with { pin, reason } when confirmed
 * @param {string} title - Dialog title
 * @param {string} description - Description of what action needs override
 * @param {boolean} requireReason - Whether reason is required
 * @param {boolean} loading - Show loading state
 */
export function ManagerPinDialog({ 
  open, 
  onClose, 
  onConfirm, 
  title = "Manager Override Required",
  description = "This action requires manager approval.",
  requireReason = true,
  loading = false
}) {
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    if (!pin || pin.length < 4) {
      setError('Please enter a valid 4-digit PIN');
      return;
    }
    
    if (requireReason && !reason.trim()) {
      setError('Please provide a reason for this action');
      return;
    }
    
    onConfirm({ pin, reason: reason.trim() });
  };

  const handleClose = () => {
    setPin('');
    setReason('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-background border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <ShieldCheck className="h-5 w-5 text-teal-500" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {description}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          
          <div className="space-y-2">
            <Label htmlFor="pin" className="text-muted-foreground">Manager PIN</Label>
            <Input
              id="pin"
              type="password"
              inputMode="numeric"
              maxLength={10}
              placeholder="Enter manager PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              className="bg-card border-border text-foreground"
              data-testid="manager-pin-input"
              autoFocus
            />
          </div>
          
          {requireReason && (
            <div className="space-y-2">
              <Label htmlFor="reason" className="text-muted-foreground">Reason</Label>
              <Textarea
                id="reason"
                placeholder="Enter reason for this action"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="bg-card border-border text-foreground resize-none"
                rows={3}
                data-testid="override-reason-input"
              />
            </div>
          )}
          
          {error && (
            <p className="text-sm text-red-600" data-testid="pin-error-message">{error}</p>
          )}
          
          <DialogFooter className="gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              disabled={loading}
              className="border-border text-muted-foreground"
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={loading}
              className="bg-secondary hover:bg-secondary/90"
              data-testid="confirm-override-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                'Confirm'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ManagerPinDialog;
